import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Loader2, Clock } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";

type DaysRow = {
  clientId: string;
  accountName: string;
  regionId: string | null;
  regionName: string;
  paidCount: number;
  avgDays: number | null;
  totalPaid: number;
};

type RegionRow = {
  regionId: string | null;
  regionName: string;
  paidCount: number;
  avgDays: number | null;
  totalPaid: number;
};

type DaysToPayResponse = {
  windowDays: number;
  clients: DaysRow[];
  regions: RegionRow[];
};

const fmtMoney = (n: number) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n);

function severity(days: number | null): "fast" | "ok" | "slow" | "very-slow" {
  if (days === null) return "ok";
  if (days <= 7) return "fast";
  if (days <= 30) return "ok";
  if (days <= 60) return "slow";
  return "very-slow";
}

function DaysBadge({ days }: { days: number | null }) {
  if (days === null) return <span className="text-muted-foreground">—</span>;
  const sev = severity(days);
  const map = {
    fast: "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border-emerald-500/30",
    ok: "bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/30",
    slow: "bg-amber-500/15 text-amber-600 dark:text-amber-400 border-amber-500/30",
    "very-slow": "bg-red-500/15 text-red-600 dark:text-red-400 border-red-500/30",
  } as const;
  return (
    <Badge variant="outline" className={map[sev]} data-testid={`badge-days-${sev}`}>
      {days.toFixed(1)}d
    </Badge>
  );
}

export function DaysToPayTab() {
  const isMobile = useIsMobile();
  const { data, isLoading } = useQuery<DaysToPayResponse>({ queryKey: ["/api/billing/days-to-pay"] });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-12">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }
  if (!data) return null;

  return (
    <div className="space-y-6" data-testid="tab-days-to-pay">
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Clock className="h-4 w-4" /> Days-to-pay by region
          </CardTitle>
          <CardDescription>
            Average days between invoice date and payment for the last {data.windowDays} days.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {data.regions.length === 0 ? (
            <p className="text-sm text-muted-foreground italic">No paid invoices in window.</p>
          ) : isMobile ? (
            <div className="space-y-2">
              {data.regions.map((r) => (
                <div
                  key={r.regionId ?? "_none"}
                  className="rounded-lg border border-border bg-card p-3"
                  data-testid={`row-region-${r.regionId ?? "none"}`}
                >
                  <div className="flex items-center justify-between gap-2 mb-2">
                    <p className="font-medium text-sm truncate">{r.regionName}</p>
                    <DaysBadge days={r.avgDays} />
                  </div>
                  <div className="flex items-center justify-between text-xs text-muted-foreground">
                    <span>{r.paidCount} paid invoices</span>
                    <span className="tabular-nums font-medium text-foreground">{fmtMoney(r.totalPaid)}</span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Region</TableHead>
                  <TableHead className="text-right">Paid invoices</TableHead>
                  <TableHead className="text-right">Avg days-to-pay</TableHead>
                  <TableHead className="text-right">Collected</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.regions.map((r) => (
                  <TableRow key={r.regionId ?? "_none"} data-testid={`row-region-${r.regionId ?? "none"}`}>
                    <TableCell className="font-medium">{r.regionName}</TableCell>
                    <TableCell className="text-right tabular-nums">{r.paidCount}</TableCell>
                    <TableCell className="text-right"><DaysBadge days={r.avgDays} /></TableCell>
                    <TableCell className="text-right tabular-nums">{fmtMoney(r.totalPaid)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Slowest payers (clients)</CardTitle>
          <CardDescription>Top 50 clients by average days-to-pay.</CardDescription>
        </CardHeader>
        <CardContent>
          {data.clients.length === 0 ? (
            <p className="text-sm text-muted-foreground italic">No paid invoices in window.</p>
          ) : isMobile ? (
            <div className="space-y-2">
              {data.clients.map((c) => (
                <div
                  key={c.clientId}
                  className="rounded-lg border border-border bg-card p-3"
                  data-testid={`row-client-${c.clientId}`}
                >
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <p className="font-medium text-sm truncate">{c.accountName}</p>
                    <DaysBadge days={c.avgDays} />
                  </div>
                  <p className="text-xs text-muted-foreground truncate mb-2">{c.regionName}</p>
                  <div className="flex items-center justify-between text-xs text-muted-foreground">
                    <span>{c.paidCount} paid invoices</span>
                    <span className="tabular-nums font-medium text-foreground">{fmtMoney(c.totalPaid)}</span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Client</TableHead>
                    <TableHead>Region</TableHead>
                    <TableHead className="text-right">Paid invoices</TableHead>
                    <TableHead className="text-right">Avg days-to-pay</TableHead>
                    <TableHead className="text-right">Collected</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.clients.map((c) => (
                    <TableRow key={c.clientId} data-testid={`row-client-${c.clientId}`}>
                      <TableCell className="font-medium truncate max-w-[260px]">{c.accountName}</TableCell>
                      <TableCell className="text-muted-foreground text-sm">{c.regionName}</TableCell>
                      <TableCell className="text-right tabular-nums">{c.paidCount}</TableCell>
                      <TableCell className="text-right"><DaysBadge days={c.avgDays} /></TableCell>
                      <TableCell className="text-right tabular-nums">{fmtMoney(c.totalPaid)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
